import React, { useContext } from 'react';
import { EngagementChartView } from './EngagementChart.view';
import { endOfMonth, format, parse, startOfMonth, startOfWeek, subDays, subMonths } from 'date-fns';
import { ChartLinePoint, getHasFillingDataProps } from '../../../LineChart/LineChart.controller';
import {
  GetPostStatsLazyQueryHookResult,
  PostStats,
  PostStatsByProfile,
  StartOfWeekFormatSetting
} from '../../../../../models/graphql';
import { AnalyticsContext } from '../../../AnalyticsProvider';
import { getIsGroupedByWeek, getQtyDaysBetweenRange } from 'modules/Analyze/helpers/chartHelpers';
import { cloneDeep } from 'lodash';
import { DateStringFormat } from 'constants/time-formats';
import { useSettings } from '../../../../../hooks/useSettings';

type EngagementChartControllerProps = {
  data: GetPostStatsLazyQueryHookResult[1]['data'];
  loading: boolean;
};

type PostMetrics = PostStats['shares'] | PostStats['clicks'] | PostStats['comments'];
const getMetricValue = (firstMetric: PostMetrics, secondMetric: PostMetrics) => {
  return typeof firstMetric === 'number' || typeof secondMetric === 'number'
    ? (firstMetric ?? 0) + (secondMetric ?? 0)
    : null;
};
const getMetricsByProfile = (postStatsByProfile: PostStatsByProfile[]): ChartLinePoint[][] => {
  return postStatsByProfile.map(profile => {
    return profile.postStats.map(postStats => {
      if (postStats.shares === null && postStats.likes === null && postStats.comments === null) {
        return { engagement: undefined };
      }
      return {
        engagement: (postStats.shares ?? 0) + (postStats.likes ?? 0) + (postStats.comments ?? 0)
      };
    });
  });
};
export const EngagementChartController = ({ data, loading }: EngagementChartControllerProps) => {
  const { selectedOrgSettings } = useSettings();
  const currentDate = new Date();
  const { selectedProfilesIds, startDate, endDate } = useContext(AnalyticsContext);
  const postStatsByProfile = data?.analyticsPostStats.postStatsByProfile ?? [];
  const copyPostStatsByProfile = cloneDeep(postStatsByProfile);

  const isWeekly = getIsGroupedByWeek(startDate, endDate);

  if (copyPostStatsByProfile.length >= 1) {
    const firstPostStat = postStatsByProfile[0].postStats;
    selectedProfilesIds.forEach(profileId => {
      const postStats = copyPostStatsByProfile.find(postStat => postStat.socialProfileId === profileId)?.postStats;
      if (!postStats) {
        return;
      }

      const { hasPreFillingData, hasPostFillingData } = getHasFillingDataProps(
        startDate,
        endDate,
        selectedOrgSettings?.startOfWeekFormat ?? StartOfWeekFormatSetting.SUNDAY
      );

      if (getQtyDaysBetweenRange(startDate, endDate) >= 101) {
        const endDateFirstDay = parse(postStats[0].rangeEndDate, DateStringFormat, currentDate);
        const startDateFirstDay = parse(postStats[0].rangeStartDate, DateStringFormat, currentDate);
        const previousDay = subDays(startDateFirstDay, 1);
        const previousMonth = subMonths(endDateFirstDay, 1);
        const mockRangeStart = isWeekly
          ? startOfWeek(previousDay, {
              weekStartsOn: selectedOrgSettings?.startOfWeekFormat === StartOfWeekFormatSetting.SUNDAY ? 0 : 1
            })
          : startOfMonth(previousMonth);
        const mockRangeEnd = isWeekly ? previousDay : endOfMonth(previousMonth);
        const mockFirstDay = { ...postStats[0] };
        mockFirstDay.rangeStartDate = format(mockRangeStart, DateStringFormat);
        mockFirstDay.rangeEndDate = format(mockRangeEnd, DateStringFormat);
        postStats.unshift(mockFirstDay);
      }

      if (hasPreFillingData || hasPostFillingData) {
        if (hasPreFillingData) {
          const originalDataSecondStat = { ...postStats[1] };
          const originalDataThirdStat = { ...postStats[2] };
          postStats[1] = {
            ...originalDataThirdStat,
            rangeStartDate: originalDataSecondStat.rangeStartDate,
            rangeEndDate: originalDataThirdStat.rangeStartDate
          };
          const likes = getMetricValue(originalDataSecondStat.likes, originalDataThirdStat.likes);
          const shares = getMetricValue(originalDataSecondStat.shares, originalDataThirdStat.shares);
          const comments = getMetricValue(originalDataSecondStat.comments, originalDataThirdStat.comments);

          postStats[2] = {
            rangeStartDate: originalDataSecondStat.rangeStartDate,
            rangeEndDate: originalDataThirdStat.rangeEndDate,
            likes,
            shares,
            comments,
            postCount: originalDataSecondStat.postCount + originalDataThirdStat.postCount
          };
        }
        if (hasPostFillingData) {
          const originalDataSecondLast = { ...postStats[postStats.length - 2] };
          const originalDataLast = { ...postStats[postStats.length - 1] };
          const likes = getMetricValue(originalDataSecondLast.likes, originalDataLast.likes);
          const shares = getMetricValue(originalDataSecondLast.shares, originalDataLast.shares);
          const comments = getMetricValue(originalDataSecondLast.comments, originalDataLast.comments);
          postStats[postStats.length - 1] = {
            rangeStartDate: originalDataSecondLast.rangeStartDate,
            rangeEndDate: originalDataLast.rangeEndDate,
            likes,
            shares,
            comments,
            postCount: originalDataSecondLast.postCount + originalDataLast.postCount
          };
        }
      }
    });

    const isEveryElementNull = copyPostStatsByProfile.every(
      profileFollowers =>
        !profileFollowers.postStats.some(
          stats => stats.comments !== null || stats.likes !== null || stats.shares !== null
        )
    );

    const dates = copyPostStatsByProfile[0].postStats.map(postObj =>
      parse(postObj.rangeEndDate, DateStringFormat, currentDate)
    );
    const firstDateFromData = copyPostStatsByProfile[0].postStats[0].rangeEndDate;
    const lastDateFromData = firstPostStat[firstPostStat.length - 1].rangeEndDate;
    const dateStartPrefillArea = copyPostStatsByProfile[0].postStats[0].rangeEndDate;
    const dateEndPrefillArea = copyPostStatsByProfile[0].postStats[1]?.rangeEndDate;

    return (
      <EngagementChartView
        isEmpty={isEveryElementNull}
        dates={dates}
        profileIds={selectedProfilesIds}
        loading={loading}
        firstDateFromData={firstDateFromData}
        lastDateFromData={lastDateFromData}
        dateStartPrefillArea={dateStartPrefillArea}
        dateEndPrefillArea={dateEndPrefillArea}
        getValuesForChartCb={() => getMetricsByProfile(copyPostStatsByProfile)}
      />
    );
  }
  return (
    <EngagementChartView
      dates={[]}
      isEmpty={true}
      loading={loading}
      profileIds={[]}
      firstDateFromData={''}
      lastDateFromData={''}
    />
  );
};
